/* eslint-disable max-len */
/* eslint-disable no-underscore-dangle */
import React, { useState } from 'react';
import Spinner from '../Spinner';
import OptionGroupAsButtons from './OptionGroupAsButtons';
import OptionGroupAsSelect from './OptionGroupAsSelect';
import OptionGroupAsRadio from './OptionGroupAsRadio';
import OptionGroupAsCombined from './OptionGroupCombined';

import { ProductFormContext } from '../../context/product-form-context';

import Helpers from './helpers';

export default function ProductOptions() {
	const mounted = React.useRef(false);

	const {
		variant,
		setVariant,
		product,
		isLoading,
		// availabilityMap,
		renderingScheme,
		sortOrder,
	} = React.useContext(ProductFormContext);

	const preselectedVariantFilters = Helpers.getFilterValuesFromVariant(variant);

	//	-----
	//	State
	//	-----
	//	Had to use stringified JSON instead of Object because React wasn't detecting the state change.
	//	The good news is that all the destringification is handled in this file; so the child components don't have any parsing/stringification to do.
	const [filters, setFilters] = useState(preselectedVariantFilters);

	//	---------------------------------
	//	State Setter (passed to Children)
	//	---------------------------------
	const setFilter = (groupIndex, optionIndex) => {
		//	Breakpoint:	Only perform this callback if the component is still mounted.
		if (!mounted.current) return;
		const tempFilters = {...filters};
		if (optionIndex === null) {
		//	Allow unsetting by passing null
			// console.log(`Unsetting OptionGroup:${ groupIndex }`);
			delete tempFilters[groupIndex];
		} else {
		//	Allow setting
			// console.log(`Setting OptionGroup:${ groupIndex } 👉 '${ optionIndex }'`);
			tempFilters[groupIndex] = optionIndex;
		}
		setFilters(tempFilters);
	};

	//	-----------
	//	Set Variant [executes after rerendering from a filter(s) change]
	//	-----------
	const currentVariant = Helpers.findVariant(product, filters);
	React.useEffect(() => {
		//	Gets set to true on component mount.
		mounted.current = true;

		setVariant && setVariant(currentVariant ?? null, true);

		//	Gets set to false on component unmount by React.
		return () => { mounted.current = false; };
	}, [setVariant, currentVariant]);

	/**
	 * BELOW useEffect will Update 'filters' when the 'variant' changes.
	 * This is alternative to the ABOVE useEffect which updates the 'variant' when the 'filters' change (i.e. user clicks an option)
	 * So now both scenarios are handled:
	 * 1. When filters get changed the corresponding variant will get set.
	 * 2. When the variant changes (from context), the corresponding filters will get set.
	 */
	React.useEffect(() => {
		if (variant && mounted.current) {
			// String comparison for equality since 'filters' is one level deep
			// and this has better performance than looping through the comparing objects properties.
			const updatedVariantFiltersString = JSON.stringify(Helpers.getFilterValuesFromVariant(variant));
			const currentFiltersString = JSON.stringify(filters);
			const areFiltersEqual = updatedVariantFiltersString === currentFiltersString;

			if (!areFiltersEqual) {
				// Set filters back as an object.
				setFilters(JSON.parse(updatedVariantFiltersString));
			}
		}
	// Don't run this effect on filter change. That already happens in the useEffect above.
	// If done here would result in infinite re-render loop.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [variant]);

	//	--------------------------
	//	Change Order Option Groups
	//	--------------------------
	const sortedOptionGroups = [];
	product?.options.forEach((optionGroup, _index) => {
		const targetPosition = sortOrder.indexOf(Helpers.slugify(optionGroup.name));
		if (targetPosition !== -1) {
			sortedOptionGroups[targetPosition] = optionGroup;
		} else {
			//	Offset the assigned position of groups with undefined order
			//	so they don't interfere with the groups that have a defined order.
			sortedOptionGroups[_index + 3] = optionGroup;
		}
	});

	//	--------------
	//	Child Elements
	//	--------------
	const optionGroups = sortedOptionGroups.map(_option => {
		const optionGroupSlug = Helpers.slugify(_option.name);

		//	If there was a sortOrder enacted... it would break the index.
		//	So we need to find the original index.
		let realIndex;
		product.options.find((optionGroup, __index) => {
			if (Helpers.slugify(optionGroup.name) === Helpers.slugify(_option.name)) {
				realIndex = __index;
				return true;
			}
			return false;
		});

		//	Find scheme to render with
		let scheme;
		//	First, look for key as a string
		scheme = renderingScheme[optionGroupSlug];
		//	Fallback to looking for key as an integer
		if (!scheme) scheme = renderingScheme[realIndex];
		//	It's okay if no scheme was found because we'll use the 'default' case.

		switch (scheme) {
		case 'select':
			return (
				<OptionGroupAsSelect
					option={_option}
					index={realIndex}
					// eslint-disable-next-line
					key={realIndex}
					setFilter={setFilter}
					setFilters={setFilters}
					filters={filters}
				/>
			);
		case 'radio':
			return (
				<OptionGroupAsRadio
					option={_option}
					index={realIndex}
					// eslint-disable-next-line
					key={realIndex}
					setFilter={setFilter}
					setFilters={setFilters}
					filters={filters}
				/>
			);
		case 'combined':
			return (
				<OptionGroupAsCombined
					option={_option}
					index={realIndex}
					// eslint-disable-next-line
					key={realIndex}
					setFilter={setFilter}
					setFilters={setFilters}
					filters={filters}
				/>
			);
		case 'button':
		case 'default':
		default:
			return (
				<OptionGroupAsButtons
					option={_option}
					index={realIndex}
					// eslint-disable-next-line
					key={realIndex}
					setFilter={setFilter}
					setFilters={setFilters}
					filters={filters}
				/>
			);
		}
	});// .map

	//	------------
	//	Early Render - If real-time availability is not yet loaded from Shopify.
	//	------------
	if (isLoading) {
		return (
			<Spinner aria-valuetext={'Loading'} />
		);
	}

	//	------------
	//	Early Render - When Product only has the 'default variant' we don't render the option. We rely on the auto-select code to select this 'default variant' on page-load.
	//	------------
	if (product?.options.length === 1 && product?.options[0].values.length === 1) {
		return null;
	}

	//	------
	//	Render - When Product has variants to choose from
	//	------
	return optionGroups || null;
}// function

// export default function ProductOptionsFactory() {
// 	const {
// 		product,
// 	} = React.useContext(ProductFormContext);

// 	if (!product)	return null;

// 	return ProductOptions;
// }
